/** One evidence section, opened from the table of contents: what it is, the
 *  console records it was built from, and the captured text exactly as the
 *  model would have read it. */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  Typography,
} from '@mui/material';

import {
  diagnoseContextApi,
  type DiagnoseContextEntry,
  type DiagnoseContextSection,
} from '@/api/diagnoseContext';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import type { Tenant } from '@/utils/gridLinks';

import { AttachmentPreview } from '../attachments/AttachmentPreview';
import { refActions, refLabel, refRoute } from './contextLinks';

interface Props {
  taskId: string;
  entry: DiagnoseContextEntry | null;
  tenant?: Tenant;
  onClose: () => void;
}

export function SectionDrawer({ taskId, entry, tenant, onClose }: Props) {
  const [page, setPage] = useState(1);

  // A different section starts at its first page, not the previous one's.
  useEffect(() => setPage(1), [entry?.name]);

  const name = entry?.name ?? '';
  const attachment = entry?.attachment ?? null;
  // An attached file's bytes come from the attachments endpoint, never from
  // `?section=`.
  const wanted = Boolean(entry?.has_text) && attachment == null;
  const query = useQuery<DiagnoseContextSection>({
    queryKey: ['ai-diag', 'context', taskId, 'section', name, page],
    queryFn: () => diagnoseContextApi.section(taskId, name, page),
    enabled: Boolean(entry) && wanted,
    staleTime: Infinity,
  });

  if (!entry) return null;

  if (attachment) {
    return (
      <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 720 }, p: 2 } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
            {attachment.filename}
          </Typography>
          <Chip size="small" label={entry.status} variant="outlined" />
          {!entry.read && <Chip size="small" variant="outlined" label="not opened by the AI" />}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Attached by the operator with question {attachment.turn} of the thread.
        </Typography>
        {entry.note && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {entry.note}
          </Alert>
        )}
        <Divider sx={{ mb: 1.5 }} />
        {entry.status === 'expired' ? (
          <Alert severity="info">This file has expired — attachments are kept for one year.</Alert>
        ) : (
          <AttachmentPreview taskId={attachment.task_id || taskId} attachment={attachment} />
        )}
      </Drawer>
    );
  }

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 720 }, p: 2 } }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
          {entry.name}
        </Typography>
        <Chip size="small" label={entry.status} variant="outlined" />
        {entry.lazy && <Chip size="small" variant="outlined" label="fetched on demand" />}
      </Stack>
      {entry.title && <Typography variant="body2">{entry.title}</Typography>}
      {entry.summary && entry.summary !== entry.title && (
        <Typography variant="caption" color="text.secondary" display="block">
          {entry.summary}
        </Typography>
      )}
      {entry.note && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {entry.note}
        </Alert>
      )}
      {entry.plan_summary && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {entry.plan_kind === 'tool' ? 'Read during the run: ' : 'Fetches: '}
          {entry.plan_summary}
        </Typography>
      )}

      {entry.refs.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="overline" color="text.secondary">
            Built from
          </Typography>
          <Stack spacing={0.25}>
            {entry.refs.map((ref) => (
              <ValueActions key={`${ref.model}:${ref.id}`} actions={refActions(ref, tenant)}>
                <Typography variant="body2" component="span">
                  {refLabel(ref)}
                  {!refRoute(ref) && (
                    <Typography variant="caption" color="text.secondary" component="span">
                      {' '}
                      (no console page)
                    </Typography>
                  )}
                </Typography>
              </ValueActions>
            ))}
          </Stack>
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {!entry.has_text ? (
        <Alert severity="info">
          {entry.lazy && !entry.read
            ? 'The model never opened this section, so nothing was captured. Run a context preview with "fetch every section" to see what it would contain.'
            : 'This section captured no text.'}
        </Alert>
      ) : query.isLoading ? (
        <CircularProgress size={20} />
      ) : query.isError ? (
        <Alert severity="error">The section text could not be loaded.</Alert>
      ) : (
        <>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              maxHeight: '60vh',
              overflow: 'auto',
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {query.data?.text}
          </Box>
          {(query.data?.pages ?? 1) > 1 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </Button>
              <Typography variant="caption">
                Page {query.data?.page} of {query.data?.pages}
              </Typography>
              <Button
                size="small"
                disabled={page >= (query.data?.pages ?? 1)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </>
      )}
    </Drawer>
  );
}
